// Hideo - the Calendar of the plan (logic without UI): it reads the publish dates and times of the videos.
// Page: src/pages/CalendarPage.vue (cards: src/components/CalCard.vue); period and view: src/stores/calendar.ts.
// - Month: one box per day (weeks from Monday) with the cards sorted by time, at most CAL_MAX_DAY, then "+N more".
// - Week: on a computer a grid of hours (8–22, extended when a video is released earlier or later), CAL_HOUR_PX pixels
//   per hour; videos less than an hour apart share the column width (lanes). On a phone a list of the 7 days.
// - The view is a device preference: localStorage "creatorOS_calview" = 'week' | anything else = month.
import { fmtDate } from '../i18n'
import { addDays, addMonths, firstOfMonth, isoDay, spanTitle, weekOfMonth } from '../utils/date'
import type { Video } from '../data/types'

export const CAL_VIEW_KEY = 'creatorOS_calview'
/** Cards shown in a day of the month (then "+N more") */
export const CAL_MAX_DAY = 3
/** Height of one hour in the week grid */
export const CAL_HOUR_PX = 56
export type CalView = 'month' | 'week'
/** Animation of the next drawing: slides in from the right / left, or rises from the bottom */
export type CalAnim = '' | 'next' | 'prev' | 'in'

/** Stored view: only 'week' counts, everything else is the month */
export const parseCalView = (raw: string | null): CalView => (raw === 'week' ? 'week' : 'month')

/** Minutes from midnight of "hh:mm", -1 without a (valid) time */
export function calMin(hhmm: string | undefined | null): number {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '')
  return m ? +m[1] * 60 + +m[2] : -1
}
/** "hh:mm" of minutes from midnight */
export const calTime = (min: number): string => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
/** Order of the videos of a day: by time (as text), videos without one last */
export const byTime = (a: Video, b: Video): number => (a.publishTime || '99').localeCompare(b.publishTime || '99')

// ===== Period =====
/** The month and the week shown */
interface CalPeriod { month: Date; week: Date }
/** Switching view stays in the same period: from a month to its first week (to this week if it is the current month),
 *  from a week to its month */
export function switchPeriod(view: CalView, p: CalPeriod, now: Date): CalPeriod {
  if (view === 'week') return { month: p.month, week: weekOfMonth(p.month, now) }
  return { month: firstOfMonth(p.week), week: p.week }
}
/** ±1 month or ±7 days */
export function navPeriod(view: CalView, p: CalPeriod, d: number): CalPeriod {
  return view === 'week' ? { month: p.month, week: addDays(p.week, d * 7) } : { month: addMonths(p.month, d), week: p.week }
}

// ===== Jumping to a month, a year, a lustrum, a decade (the title's menu, CalJump.vue) =====
/** Levels of the jump menu: the months of a year, the years of a decade (in its two lustra), the decades of a century
 *  (each with its two lustra) */
export type JumpLevel = 'months' | 'years' | 'decades'
/** First year of the lustrum (5 years: 2025–2029), of the decade (2020–2029) and of the century (2000–2099) of a year */
export const lustrumOf = (y: number): number => y - (((y % 5) + 5) % 5)
export const decadeOf = (y: number): number => y - (((y % 10) + 10) % 10)
export const centuryOf = (y: number): number => y - (((y % 100) + 100) % 100)
/** "2025–2029": a span of years (last year included) */
export const yearSpan = (first: number, years: number): string => `${first}–${first + years - 1}`
/** Videos per publish year and per month ("yyyy" and "yyyy-mm") */
export function periodCounts(videos: readonly Pick<Video, 'publishDate'>[]): Map<string, number> {
  const out = new Map<string, number>()
  const add = (k: string) => out.set(k, (out.get(k) || 0) + 1)
  videos.forEach(v => {
    const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(v.publishDate || '')
    if (m) { add(m[1]); add(`${m[1]}-${m[2]}`) }
  })
  return out
}
/** Videos of a span of years in the counts of periodCounts */
export const spanCount = (counts: Map<string, number>, first: number, years: number): number =>
  Array.from({ length: years }, (_, i) => counts.get(String(first + i)) || 0).reduce((a, b) => a + b, 0)
/** The period shown after jumping to a month: that month, and its first week (this week for the current month) */
export const jumpPeriod = (year: number, month: number, now: Date): CalPeriod => {
  const m = new Date(year, month, 1)
  return { month: m, week: weekOfMonth(m, now) }
}

// ===== Month =====
/** Boxes of a month from Monday: 0 = empty box before the 1st, otherwise the day number */
export function monthCells(month: Date): number[] {
  const y = month.getFullYear(), m = month.getMonth(), first = (new Date(y, m, 1).getDay() + 6) % 7, days = new Date(y, m + 1, 0).getDate()
  return [...Array<number>(first).fill(0), ...Array.from({ length: days }, (_, i) => i + 1)]
}
/** yyyy-mm-dd of a day of a month */
export const monthIso = (month: Date, day: number): string => isoDay(new Date(month.getFullYear(), month.getMonth(), day))
/** Videos of a day, sorted by time */
export const dayVideos = (videos: Video[], iso: string): Video[] => videos.filter(v => v.publishDate === iso).sort(byTime)
/** Title of a month ("ottobre 2026") */
export const monthTitle = (month: Date): string => fmtDate(month, { month: 'long', year: 'numeric' })

// ===== Week =====
/** The 7 days of the week starting on this Monday */
export const weekDays = (monday: Date): Date[] =>
  Array.from({ length: 7 }, (_, i) => addDays(monday, i))
/** Title of a week: "12–18 ott 2026", or "28 set – 4 ott 2026" across two months */
export const weekTitle = (days: Date[]): string => spanTitle(days[0], days[6])
/** Hours of the grid: 8–22, extended to the earliest and latest release time (minutes from midnight) */
export function hourRange(times: number[]): { startH: number; endH: number; hours: number[] } {
  const startH = Math.min(8, ...times.map(m => Math.floor(m / 60))), endH = Math.max(22, ...times.map(m => Math.floor(m / 60) + 1))
  return { startH, endH, hours: Array.from({ length: endH - startH }, (_, i) => startH + i) }
}
/** A video placed in a week column: minutes, lane and lanes of its group (videos less than an hour apart) */
interface Placed<T> { item: T; t: number; lane: number; lanes: number }
/** Lanes of the timed videos of a day (sorted by time): each video takes the first lane free for an hour; the width
 *  is shared by the videos less than an hour apart */
export function placeLanes<T>(timed: { item: T; t: number }[]): Placed<T>[] {
  const ends: number[] = []
  const placed = timed.map(x => {
    let l = ends.findIndex(end => end <= x.t)
    if (l < 0) { l = ends.length; ends.push(0) }
    ends[l] = x.t + 60
    return { ...x, lane: l }
  })
  return placed.map(x => ({ ...x, lanes: Math.max(...placed.filter(y => Math.abs(y.t - x.t) < 60).map(y => y.lane)) + 1 }))
}
/** Top of a time in the grid, in pixels */
export const minTop = (min: number, startH: number): number => (min - startH * 60) / 60 * CAL_HOUR_PX
/** Time of a point in a week column (y pixels from its top), in half-hour steps, at most 23:30 */
export const slotMin = (y: number, startH: number): number => Math.min(23 * 60 + 30, startH * 60 + Math.floor(Math.max(0, y) / CAL_HOUR_PX * 2) * 30)

// ===== Dropping a card =====
/** A card dropped on a day of the month: only the date changes (the time stays). true = changed, to save */
export function dropOnDay(v: Video, iso: string): boolean {
  if (v.publishDate === iso) return false
  v.publishDate = iso
  return true
}
/** A card dropped in the week: date and time ('' = the "no time" row: only the date changes). true = changed */
export function dropOnWeek(v: Video, iso: string, time: string): boolean {
  if (v.publishDate === iso && (!time || v.publishTime === time)) return false
  v.publishDate = iso
  if (time) v.publishTime = time
  return true
}
